import type { ResourceDictionary } from "Infrastructure/Translations/ResourceDictionary";
import { Resources } from "Infrastructure/Translations/Resources";
import { MAX_CLIENT_AGE_YEARS, getMinBirthDate } from "Infrastructure/Utils/ClientUtils";
import type { ObjPathProxy } from "ts-object-path";
import { type ObjectSchema, date, object, string } from "yup";

export type ClientDetailsFormModel = {
  readonly firstName: string;
  readonly lastName: string;
  readonly academicDegree?: string;
  readonly birthDate: Date;
  readonly email: string;
  readonly prefix: string;
  readonly phone: string;
  readonly street?: string;
  readonly descriptionNumber?: string;
  readonly orientationNumber?: string;
  readonly registrationNumber?: string;
  readonly city?: string;
  readonly postalCode?: string;
  readonly countryCode?: string;
  readonly identificationNumber?: string | null;
};

export type ClientDetailsFormData = {
  readonly firstName: string;
  readonly lastName: string;
  readonly titlePrefix?: string;
  readonly titlePostfix?: string;
  readonly birthDate: Date;
  readonly email: string;
  readonly prefix: string;
  readonly phone: string;
  readonly street?: string;
  readonly streetNumber?: string;
  readonly houseNumber?: string;
  readonly city?: string;
  readonly postalCode?: string;
  readonly countryCode?: string;
  readonly personalIdentificationNumber?: string | null;
};

const phoneRegExp = /^(\d{9})|(\d{10})|(\d{11})$/;
const postalCodeRegExp = /^(\d{5})?$/;
const emailRegExp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const getYesterday = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d;
};

export const clientDetailsFormSchema: (
  t: (resourcePath: ObjPathProxy<ResourceDictionary, string>, options?: any) => string,
) => ObjectSchema<ClientDetailsFormData> = (t) => {
  return object({
    firstName: string().required(t(Resources.Validation.Required)),
    lastName: string().required(t(Resources.Validation.Required)),
    titlePrefix: string(),
    titlePostfix: string(),
    birthDate: date()
      .nullable()
      .typeError(t(Resources.Validation.BadValue))
      .required(t(Resources.Validation.Required))
      .min(getMinBirthDate(), t(Resources.Validation.BirthDateTooOld, { years: MAX_CLIENT_AGE_YEARS }))
      .max(getYesterday(), t(Resources.Validation.MustBeOlderThanOneDay)),
    email: string().matches(emailRegExp, t(Resources.Validation.InvalidEmail)).required(t(Resources.Validation.Required)),
    phone: string().matches(phoneRegExp, t(Resources.Validation.InvalidPhone)).required(t(Resources.Validation.Required)),
    prefix: string().required(t(Resources.Validation.Required)),
    street: string(),
    streetNumber: string(),
    houseNumber: string(),
    city: string(),
    postalCode: string().matches(postalCodeRegExp, t(Resources.Validation.InvalidFormat)),
    countryCode: string(),
    personalIdentificationNumber: string().nullable(),
  }).defined();
};
